import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CustomSelect from '../CustomSelect/CustomSelect';
import useLocalizedMoney from '../../hooks/useLocalizedMoney';
import { formatPriceWithMinMax, getValueByLanguage, isEmptyObject } from '../../utils';
import { PRICE_TYPE } from '../../utils/Constants';
import TextBreaks from '../TextBreaks/TextBreaks';
import icDown from '../../assets/imgs/icDown.svg?react';
import i18n from '../../assets/languages/configs';
import styles from './styles.module.css';
import './styles.css';
import { SelectNumber as SelectNumberT, TicketInfo, MultilingualType } from 'types';

type SelectNumberProps = {
    ticketInfo: TicketInfo;
    onChange: (number: SelectNumberT | null) => void;
    customDefaultNumber: SelectNumberT | null;
    isReserve?: boolean;
    isReserveTicketSelected?: boolean;
    onClick?: () => void;
};

const SelectNumber = memo(
    ({
        ticketInfo,
        onChange,
        customDefaultNumber,
        isReserve,
        isReserveTicketSelected,
        onClick = () => {},
    }: SelectNumberProps) => {
        const { formatMoney } = useLocalizedMoney();
        const { t } = useTranslation();
        const { language } = i18n;
        const l = useCallback((text?: MultilingualType | null) => getValueByLanguage(text, language), [language]);
        const customNumber = useMemo(() => customDefaultNumber ?? {}, [customDefaultNumber]);

        const [number, setNumber] = useState(() => {
            // 初期値設定
            if (isEmptyObject(customNumber)) {
                const { isInUseAdult, isInUseCustom1, isInUseCustom2, isInUseCustom3 } = ticketInfo;

                if (isInUseAdult) {
                    customNumber.adult =
                        ticketInfo.maxCountAdult === 0
                            ? 0
                            : Math.max(1, Math.min(ticketInfo.minCountAdult ?? 0, ticketInfo.maxCountAdult ?? 9));
                }
                if (isInUseCustom1) {
                    customNumber.custom1 =
                        ticketInfo.maxCountCustom1 === 0
                            ? 0
                            : Math.max(0, Math.min(ticketInfo.minCountCustom1 ?? 0, ticketInfo.maxCountCustom1 ?? 9));
                }
                if (isInUseCustom2) {
                    customNumber.custom2 =
                        ticketInfo.maxCountCustom2 === 0
                            ? 0
                            : Math.max(0, Math.min(ticketInfo.minCountCustom2 ?? 0, ticketInfo.maxCountCustom2 ?? 9));
                }
                if (isInUseCustom3) {
                    customNumber.custom3 =
                        ticketInfo.maxCountCustom3 === 0
                            ? 0
                            : Math.max(0, Math.min(ticketInfo.minCountCustom3 ?? 0, ticketInfo.maxCountCustom3 ?? 9));
                }
            }
            return customNumber;
        });
        const [openDropdown, setIsOpenDropdown] = useState(isReserve && isReserveTicketSelected ? { open: false } : {});

        useEffect(() => {
            onChange(number);
        }, [number, onChange]);

        useEffect(() => {
            if (isReserve && isReserveTicketSelected) {
                setIsOpenDropdown({ open: false });
            } else {
                setIsOpenDropdown({});
            }
        }, [isReserve, isReserveTicketSelected]);

        const selectOptions = useMemo(() => {
            const personOptionsFormat = (minCount: number, maxCount: number) => {
                const personOptions = [];
                const minSelected = minCount ?? 0;
                const maxSelected = maxCount ?? 9;

                if (maxSelected === 0 || minSelected > maxSelected) {
                    personOptions.push({
                        label: t('ticket-detail.select-number', { number: maxSelected }),
                        value: maxSelected,
                    });
                } else {
                    for (let number = minSelected; number <= maxSelected; number++) {
                        personOptions.push({
                            label: t('ticket-detail.select-number', { number: number }),
                            value: number,
                        });
                    }
                }

                return personOptions;
            };

            const options = [];

            const { isInUseAdult, isInUseCustom1, isInUseCustom2, isInUseCustom3 } = ticketInfo;
            const {
                priceAdultMin,
                priceAdultMax,
                priceCustom1Min,
                priceCustom1Max,
                priceCustom2Min,
                priceCustom2Max,
                priceCustom3Min,
                priceCustom3Max,
            } = ticketInfo;

            if (isInUseAdult) {
                let price = formatMoney(ticketInfo.priceAdult, 'JPY');
                if (ticketInfo.priceType === PRICE_TYPE.ADDITION) {
                    price = formatPriceWithMinMax({ minPrice: priceAdultMin, maxPrice: priceAdultMax }, formatMoney);
                }
                options.push({
                    key: 'adult',
                    label: t('ticket-detail.adult'),
                    subLabel: null,
                    price: price,
                    defaultNumber: customNumber?.adult,
                    personOptions: personOptionsFormat(ticketInfo?.minCountAdult ?? 0, ticketInfo?.maxCountAdult ?? 9),
                });
            }
            if (isInUseCustom1) {
                let price = formatMoney(ticketInfo.priceCustom1, 'JPY');
                if (ticketInfo.isReserve && ticketInfo.priceType === PRICE_TYPE.ADDITION) {
                    price = formatPriceWithMinMax(
                        { minPrice: priceCustom1Min, maxPrice: priceCustom1Max },
                        formatMoney,
                    );
                }
                options.push({
                    key: 'custom1',
                    label: l(ticketInfo.custom1Name),
                    subLabel: l(ticketInfo?.custom1Note),
                    price: price,
                    defaultNumber: customNumber?.custom1,
                    personOptions: personOptionsFormat(
                        ticketInfo?.minCountCustom1 ?? 0,
                        ticketInfo.maxCountCustom1 ?? 9,
                    ),
                });
            }
            if (isInUseCustom2) {
                let price = formatMoney(ticketInfo.priceCustom2, 'JPY');
                if (ticketInfo.isReserve && ticketInfo.priceType === PRICE_TYPE.ADDITION) {
                    price = formatPriceWithMinMax(
                        { minPrice: priceCustom2Min, maxPrice: priceCustom2Max },
                        formatMoney,
                    );
                }
                options.push({
                    key: 'custom2',
                    label: l(ticketInfo.custom2Name),
                    subLabel: l(ticketInfo?.custom2Note),
                    price: price,
                    defaultNumber: customNumber?.custom2,
                    personOptions: personOptionsFormat(
                        ticketInfo?.minCountCustom2 ?? 0,
                        ticketInfo.maxCountCustom2 ?? 9,
                    ),
                });
            }
            if (isInUseCustom3) {
                let price = formatMoney(ticketInfo.priceCustom3, 'JPY');
                if (ticketInfo.isReserve && ticketInfo.priceType === PRICE_TYPE.ADDITION) {
                    price = formatPriceWithMinMax(
                        { minPrice: priceCustom3Min, maxPrice: priceCustom3Max },
                        formatMoney,
                    );
                }
                options.push({
                    key: 'custom3',
                    label: l(ticketInfo.custom3Name),
                    subLabel: l(ticketInfo?.custom3Note),
                    price: price,
                    defaultNumber: customNumber?.custom3,
                    personOptions: personOptionsFormat(
                        ticketInfo.minCountCustom3 ?? 0,
                        ticketInfo.maxCountCustom3 ?? 9,
                    ),
                });
            }
            return options;
        }, [ticketInfo, customNumber, formatMoney, l, t]);

        const handleChange = (key: keyof SelectNumberT, value: number) => {
            setNumber((prev) => {
                return { ...prev, [key]: value };
            });
        };

        return (
            <>
                {selectOptions.map((item, index) => {
                    return (
                        <div key={index} className={styles.selectionView}>
                            <div className={styles.label}>
                                <p>{`${item.label} / ${item.price}`}</p>
                                {item?.subLabel && (
                                    <p className={styles.subLabel}>
                                        <TextBreaks text={`(${item.subLabel})`} />
                                    </p>
                                )}
                            </div>
                            <CustomSelect
                                listHeight={200}
                                size="large"
                                variant="plain"
                                defaultValue={item.defaultNumber}
                                className={`${styles.selectPerson} select-number`}
                                options={item.personOptions}
                                suffixIcon={<DownOutlined />}
                                onChange={(number: number) => handleChange(item.key, number)}
                                getPopupContainer={(trigger: HTMLElement) => trigger.parentNode}
                                onClick={onClick}
                                {...openDropdown}
                            />
                        </div>
                    );
                })}
            </>
        );
    },
);
function DownOutlined() {
    return <div>{React.createElement(icDown, { className: styles.downIcon })}</div>;
}

export default SelectNumber;
